import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

import { createParsers, serialize } from './utils';
import type { UrlStateOptions, SerializableValue, UrlState, UseUrlStateReturn } from './types';

/**
 * Hook for managing state in URL search parameters
 * @param defaultValues - Default values for the state
 * @param options - Options for URL state management
 * @returns [state, setState, resetState] tuple
 */
export function useUrlState<T extends UrlState>(defaultValues: T, options?: UrlStateOptions): UseUrlStateReturn<T> {
  const [searchParams, setSearchParams] = useSearchParams();
  const parsers = useMemo(() => createParsers(defaultValues), [defaultValues]);

  const state = useMemo(() => {
    const entries = Object.entries(defaultValues).map(([key, defaultValue]) => {
      const urlValue = searchParams.get(key);
      const value = urlValue !== null ? parsers[key as keyof T](urlValue) : defaultValue;
      return [key, value];
    });

    return Object.fromEntries(entries) as T;
  }, [searchParams, defaultValues, parsers]);

  const setState = useCallback(
    (updates: Partial<T> | ((prev: T) => Partial<T>)) => {
      setSearchParams(
        (prev) => {
          const newState = typeof updates === 'function' ? updates(state) : updates;

          const newParams = new URLSearchParams(prev);

          Object.entries(newState).forEach(([key, value]) => {
            const shouldRemove = value === undefined || value === defaultValues[key as keyof T];
            if (shouldRemove) {
              newParams.delete(key);
            } else {
              newParams.set(key, serialize(value as SerializableValue));
            }
          });

          return newParams;
        },
        { replace: options?.replace ?? true }
      );
    },
    [setSearchParams, state, defaultValues, options?.replace]
  );

  const resetState = useCallback(
    (keys?: Array<keyof T>) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          const keysToReset = keys?.map(String) || Object.keys(defaultValues);
          keysToReset.forEach((key) => newParams.delete(key));
          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams, defaultValues]
  );

  return [state, setState, resetState];
}
